import { useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { TESTIMONIALS } from '../constants';

const DURATION = 9000;

export default function Testimonials() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const reduce = useReducedMotion();

  useEffect(() => {
    if (paused || reduce) return undefined;
    const t = setTimeout(() => setIndex((i) => (i + 1) % TESTIMONIALS.length), DURATION);
    return () => clearTimeout(t);
  }, [index, paused, reduce]);

  const current = TESTIMONIALS[index];

  return (
    <section
      id="testimonials"
      aria-labelledby="clients-title"
      className="relative py-24 sm:py-32"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="frame grid gap-12 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-4">
          <h2 id="clients-title" className="h-section">
            In their words
          </h2>
          <ul className="mt-10 border-t border-line" role="tablist" aria-label="Client testimonials">
            {TESTIMONIALS.map((t, i) => {
              const active = i === index;
              return (
                <li key={t.id} className="relative border-b border-line">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={active}
                    aria-controls="testimonial-panel"
                    onClick={() => setIndex(i)}
                    className={`w-full py-5 text-left transition-colors ${active ? 'text-ink' : 'text-muted hover:text-ink'}`}
                  >
                    <span className="block font-semibold">{t.name}</span>
                    <span className="block text-sm text-muted">{t.role}</span>
                  </button>
                  {active && (
                    <motion.span
                      key={`bar-${index}-${paused}`}
                      className="absolute -bottom-px left-0 h-px w-full origin-left bg-accent"
                      initial={{ scaleX: paused || reduce ? 1 : 0 }}
                      animate={{ scaleX: 1 }}
                      transition={{ duration: paused || reduce ? 0 : DURATION / 1000, ease: 'linear' }}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        <div id="testimonial-panel" role="tabpanel" aria-live="polite" className="lg:col-span-8 lg:pt-4">
          <AnimatePresence mode="wait">
            <motion.figure
              key={current.id}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <svg width="56" height="40" viewBox="0 0 56 40" aria-hidden="true" className="text-accent">
                <path
                  fill="currentColor"
                  d="M0 40V24C0 10 7 2 21 0l2 6c-7 2-11 6-11 12h10v22H0Zm32 0V24c0-14 7-22 21-24l2 6c-7 2-11 6-11 12h10v22H32Z"
                />
              </svg>
              {current.rating && (
                <p className="mt-6 text-xl tracking-[0.2em] text-[#F5B529]" aria-label={`${current.rating} out of 5 stars`}>
                  {'\u2605'.repeat(current.rating)}
                </p>
              )}
              <blockquote
                className={`display-semi ${current.rating ? 'mt-4' : 'mt-8'} ${
                  current.content.length > 260
                    ? 'max-w-[46ch] text-[1.2rem] leading-[1.35] sm:text-2xl lg:text-[1.75rem]'
                    : 'max-w-[28ch] text-[1.75rem] leading-[1.2] sm:text-4xl lg:text-[2.75rem]'
                }`}
              >
                {current.content}
              </blockquote>
              <figcaption className="mt-10 flex items-center gap-4">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent text-sm font-bold text-accent-ink">
                  {current.avatar}
                </span>
                <span>
                  <span className="block font-semibold">{current.name}</span>
                  <span className="block text-sm text-muted">{current.role}</span>
                </span>
              </figcaption>
            </motion.figure>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
